/* frame-less, responsive flow controller + success screen.
   Replaces the prototype's device-framed layouts: the form now adapts to its
   own container width (true container responsiveness) and flows naturally in
   the host page, so it embeds cleanly anywhere. */
import React, { useState as useS, useEffect as useE, useRef as useR, useLayoutEffect } from "react";
import { BrandMark } from "../components/BrandMark.jsx";
import { Icon, Llama } from "../components/Icon.jsx";
import { BRAND, entityLine } from "../brand.js";
import { getSteps, getProduct, INITIAL } from "../data/data.js";
import { getPanels } from "./steps.jsx";
import { resources } from "../resources.js";

const AUTO_KEYS = new Set(["existing", "marital", "residency", "status", "term", "freq", "purpose"]);
const WIDE_BREAKPOINT = 880;

/* ---------- progress ---------- */
/* Honest time estimate (spec §10.4): per-step weights rather than a flat
   guess — the bank-connect and business steps genuinely take longer. */
const STEP_SECONDS = {
  lead: 60, vehicle: 45, purpose: 40, entity: 90, situation: 60, parties: 120,
  financials: 90, customer: 60, identity: 45, needs: 30, bank: 90, income: 60,
  assets: 60, review: 60,
};
const minutesRemaining = (steps, stepIndex) => {
  const secs = steps.slice(stepIndex).reduce((t, s) => t + (STEP_SECONDS[s.id] || 45), 0);
  return Math.max(1, Math.ceil(secs / 60));
};

/* Calm, always-visible save status (spec §10.5). It never claims "saved"
   unless the server confirmed; a browser-only draft says exactly that. */
function SaveStatus({ saveState, compact }) {
  if (!saveState || saveState.state === "idle") return null;
  const base = { display: "inline-flex", alignItems: "center", gap: 5, fontSize: compact ? 11.5 : 12, fontWeight: 600, whiteSpace: "nowrap" };
  if (saveState.state === "saving") {
    return <span style={{ ...base, color: "var(--ink-45)" }} role="status">
      <span className="pf-spin" style={{ width: 11, height: 11, border: "2px solid var(--line-2)", borderTopColor: "var(--coral)", borderRadius: "50%", flexShrink: 0 }} />
      Saving…
    </span>;
  }
  if (saveState.state === "saved") {
    return <span style={{ ...base, color: "var(--success)" }} role="status"><Icon name="check" size={12} /> Saved just now</span>;
  }
  if (saveState.state === "local") {
    return <span style={{ ...base, color: "var(--ink-45)" }} role="status"><Icon name="check" size={12} /> Saved on this device</span>;
  }
  return <span style={{ ...base, color: "var(--warning)" }} role="status"><Icon name="info" size={12} /> Unable to save - retrying</span>;
}

function ProgressBar({ stepIndex, frac, compact, steps, saveState }) {
  const step = steps[stepIndex];
  const mins = minutesRemaining(steps, stepIndex);
  return (
    <div style={{ width: "100%" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8, gap: 12 }}>
        <span style={{ fontSize: compact ? 12.5 : 13, fontWeight: 600, color: "var(--ink-45)", letterSpacing: ".02em", whiteSpace: "nowrap" }}>
          Step {stepIndex + 1} of {steps.length}
        </span>
        <span style={{ fontSize: compact ? 12.5 : 13, fontWeight: 600, color: "var(--coral)", whiteSpace: "nowrap" }}>{step.kicker}</span>
      </div>
      <div className="pf-progbar"><div className="pf-progbar__fill" style={{ width: `${Math.max(4, frac * 100)}%` }} /></div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginTop: 6, gap: 12, minHeight: 16 }}>
        <span style={{ fontSize: compact ? 11.5 : 12, color: "var(--ink-45)", whiteSpace: "nowrap" }}>
          About {mins} minute{mins === 1 ? "" : "s"} remaining
        </span>
        <SaveStatus saveState={saveState} compact={compact} />
      </div>
    </div>
  );
}

/* ---------- trust footer ----------
   Asset finance is written by a DIFFERENT legal entity, and business-purpose
   lending sits outside the NCCP — showing a consumer credit licence number on
   a commercial application would misstate who the applicant is dealing with. */
function TrustFooter({ pad = "10px 0 0", product = "car" }) {
  const entity = entityLine(product);
  return (
    <div style={{ padding: pad, textAlign: "center" }}>
      <div className="pf-trust" style={{ justifyContent: "center", fontSize: 11.5 }}>
        <Icon name="lock" size={13} style={{ color: "var(--success)" }} />
        {entity} · 256-bit SSL
      </div>
    </div>
  );
}

/* ---------- nav buttons ---------- */
function Nav({ onBack, onNext, isFirst, isLast, valid, submitting, ctaLabel }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      {!isFirst && (
        <button className="pf-back" onClick={onBack} aria-label="Back" style={{ flexShrink: 0, border: "1.5px solid var(--line-2)" }}>
          <Icon name="arrowLeft" size={20} />
        </button>
      )}
      {/* Always clickable: an unmet step answers the click with focused,
          per-question error messages instead of a silently dead button.
          Deliberately NO aria-disabled - the button genuinely responds, and
          claiming "disabled" to assistive tech would be a lie that also stops
          it announcing the role=alert messages the click produces. */}
      <button className="pf-btn pf-btn--full" disabled={submitting} onClick={onNext}>
        {submitting ? <span className="pf-spin" style={{ width: 18, height: 18, border: "2.5px solid rgba(255,255,255,.4)", borderTopColor: "#fff", borderRadius: "50%" }} />
          : <React.Fragment>{ctaLabel}{!isLast && <Icon name="arrowRight" size={19} />}</React.Fragment>}
      </button>
    </div>
  );
}

/* ---------- one panel block (heading style varies by mode) ---------- */
/* `invalid` = the applicant tried to continue while this question is
   unanswered/incomplete: mark the block (red field borders via CSS, a
   data-invalid hook for focus management) and say what's needed in plain
   words. role="alert" announces it to screen readers the moment it appears;
   it clears live as soon as the answer becomes valid. */
function PanelBlock({ panel, mode, first, invalid }) {
  const headingSize = mode === "focus" ? 27 : 19;
  const errMsg = invalid ? (typeof panel.err === "function" ? panel.err() : panel.err) || "This one's needed before you can continue." : null;
  return (
    <div style={{ marginTop: first ? 0 : (mode === "focus" ? 0 : 26) }}
         className={invalid ? "pf-panel--error" : undefined}
         data-invalid={invalid ? "true" : undefined}>
      {!panel.hide && (
        <div style={{ marginBottom: 18 }}>
          <h2 style={{ fontSize: headingSize, lineHeight: 1.15 }}>{panel.q}</h2>
          {panel.hint && <p className="pf-hint" style={{ fontSize: 14, marginTop: 8 }}>{panel.hint}</p>}
        </div>
      )}
      {panel.render()}
      {invalid && (
        <div className="pf-panel-err" role="alert">
          <Icon name="info" size={16} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>{errMsg}</span>
        </div>
      )}
    </div>
  );
}

/* ---------- screen content (per direction) ---------- */
function ScreenContent({ step, panels, mode, focusPanel, attempted }) {
  const bad = (p) => attempted && !p.valid();
  if (mode === "focus") {
    const p = panels[focusPanel];
    return <PanelBlock panel={p} mode="focus" first invalid={bad(p)} />;
  }
  if (mode === "sectioned") {
    return (
      <div>
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ fontSize: 26 }}>{step.title}</h1>
        </div>
        {panels.map((p, i) => {
          const showGroup = p.group && (i === 0 || panels[i - 1].group !== p.group);
          return (
            <div key={p.key}>
              {showGroup && <div className="pf-group-head" style={{ marginTop: i === 0 ? 0 : 28, marginBottom: 14, paddingBottom: 8, borderBottom: "1px solid var(--line)" }}>{p.group}</div>}
              <PanelBlock panel={p} mode="sectioned" first={i === 0 && !showGroup} invalid={bad(p)} />
            </div>
          );
        })}
      </div>
    );
  }
  /* flow — progressive reveal */
  let reveal = panels.length;
  for (let i = 0; i < panels.length; i++) { if (!panels[i].valid()) { reveal = i + 1; break; } }
  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 26 }}>{step.title}</h1>
      </div>
      {panels.slice(0, reveal).map((p, i) => (
        <div key={p.key} className={i === reveal - 1 && reveal < panels.length ? "pf-reveal" : ""}>
          <PanelBlock panel={p} mode="flow" first={i === 0} invalid={bad(p)} />
        </div>
      ))}
    </div>
  );
}

/* ============================================================
   Success screen
   ============================================================ */
function SuccessScreen({ f, onReset, mascot, compact, reference }) {
  const ref = reference || (`${BRAND.referencePrefix || "APP"}-` + (Date.now() % 1000000).toString().padStart(6, "0"));
  const steps = [
    { icon: "check", text: "Everything's in - there's nothing more you need to do right now." },
    { icon: "search", text: "Our team reviews your details and may call if we need a quick clarification." },
    { icon: "calendar", text: "We'll be in touch shortly with next steps." },
  ];
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", padding: compact ? "32px 24px 40px" : "52px 40px", gap: 4 }}>
      {mascot !== "off" && <div className="pf-pop" style={{ animation: "pf-pop .6s both" }}><Llama size={compact ? 176 : 228} /></div>}
      <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginTop: 8, padding: "6px 14px", borderRadius: 99, background: "var(--success-tint)", color: "var(--success)", fontWeight: 600, fontSize: 13.5 }}>
        <Icon name="check" size={16} /> Application received
      </div>
      <h1 style={{ fontSize: compact ? 27 : 34, marginTop: 16 }}>Thank you, {f.firstName || "mate"}!</h1>
      <p style={{ fontSize: compact ? 15 : 16.5, color: "var(--ink-70)", maxWidth: 440, marginTop: 12, lineHeight: 1.5 }}>
        We've got your application{f.email ? <> - we'll reach out at <strong>{f.email}</strong></> : ""}. Here's what happens next:
      </p>

      <div style={{ width: "100%", maxWidth: 440, marginTop: 22, display: "flex", flexDirection: "column", gap: 10, textAlign: "left" }}>
        {steps.map((s, i) => (
          <div key={i} className="pf-card" style={{ display: "flex", alignItems: "center", gap: 12, padding: "13px 15px" }}>
            <span style={{ width: 30, height: 30, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "var(--coral-tint-2)", color: "var(--coral)" }}>
              <Icon name={s.icon} size={16} />
            </span>
            <span style={{ fontSize: compact ? 13.5 : 14.5, color: "var(--ink-70)", lineHeight: 1.4 }}>{s.text}</span>
          </div>
        ))}
      </div>

      <div className="pf-chip" style={{ marginTop: 22, height: 38, fontSize: 14, whiteSpace: "nowrap" }}>Reference <strong style={{ marginLeft: 6 }}>{ref}</strong></div>
      {BRAND.phone && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 18, fontSize: 13.5, color: "var(--ink-45)" }}>
          <Icon name="phone" size={15} /> Questions? Call the {BRAND.name} team on {BRAND.phone}
        </div>
      )}
      <button className="pf-btn pf-btn--ghost" style={{ marginTop: 22 }} onClick={onReset}>Start a new application</button>
    </div>
  );
}

/* ============================================================
   FlowApp — owns position + nav, responsive (no device frame)
   ============================================================ */
export function FlowApp({ f, set, replace, direction = "sectioned", mascot = "milestones", product = "car", profile = null, saveState = null, onStepComplete, onSubmit, onInitiateBankSession, onBankSessionStatus, onBankSessionEvent, onAbnLookup, onSupplyStatements, resumeAfterStepId = null }) {
  // profile (src/data/profiles.js) bundles the product+channel variation;
  // absent (direct component use), fall back to the product's own config.
  const steps = profile ? profile.steps : getSteps(product);
  const prodCfg = profile ? profile.productCfg : getProduct(product);
  // resumeAfterStepId is the last COMPLETED step of a resumed draft — start on
  // the step after it (capped at the final step).
  const [stepIndex, setStepIndex] = useS(() => {
    if (!resumeAfterStepId) return 0;
    const i = steps.findIndex((s) => s.id === resumeAfterStepId);
    return i >= 0 ? Math.min(i + 1, steps.length - 1) : 0;
  });
  const [focusPanel, setFocusPanel] = useS(0);
  const [dir, setDir] = useS("fwd");
  const [attempted, setAttempted] = useS(false); // tried to continue with unmet questions
  const [submitting, setSubmitting] = useS(false);
  const [done, setDone] = useS(false);
  const [error, setError] = useS(null);
  const [result, setResult] = useS(null);
  const scrollRef = useR(null);

  /* container responsiveness — measure our own width, not the viewport,
     so the form lays out correctly even inside a narrow column. */
  const wrapRef = useR(null);
  const [isWide, setIsWide] = useS(typeof window !== "undefined" ? window.innerWidth >= WIDE_BREAKPOINT : true);
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => setIsWide(el.getBoundingClientRect().width >= WIDE_BREAKPOINT);
    measure();
    let ro;
    if (typeof ResizeObserver !== "undefined") { ro = new ResizeObserver(measure); ro.observe(el); }
    else window.addEventListener("resize", measure);
    return () => { if (ro) ro.disconnect(); else window.removeEventListener("resize", measure); };
  }, []);
  const device = isWide ? "desktop" : "phone";

  const ctx = {
    goToStep: (num) => { setDir("back"); setStepIndex(num - 1); setFocusPanel(0); },
    onIllionComplete: () => goNext(),
    // Step 6: creates the lender application & returns the bank-statements URL
    initiateBankSession: onInitiateBankSession ? () => onInitiateBankSession(f) : null,
    // Step 6: polls whether illion has webhooked the statements data back yet
    bankSessionStatus: onBankSessionStatus ? () => onBankSessionStatus(f) : null,
    // Step 6: relays illion's browser postMessage events (bank_selected,
    // submission_complete, ...) to the server for ops visibility - these
    // only ever exist in the browser, never as server-side webhooks
    relayBankEvent: onBankSessionEvent ? (event) => onBankSessionEvent(f, event) : null,
    // Asset finance, entity step: the ABN "Validate" lookup against the
    // Australian Business Register (server-side - the credential never
    // reaches this bundle).
    lookupAbn: onAbnLookup || null,
    // Asset finance, bank step: a broker who already holds the illion
    // statements hands them over here instead of running the flow.
    supplyStatements: onSupplyStatements ? (payload) => onSupplyStatements(f, payload) : null,
    // The RESOLVED product, not the requested one: with asset finance switched
    // off, ?product=commercial falls back to a car loan, and panels keyed off
    // ctx.product (review sections, consent set) must follow the flow the
    // applicant is actually filling in — or they'd be shown commercial
    // consents the server would then reject.
    product: prodCfg.id, amountMin: prodCfg.amountMin, amountMax: prodCfg.amountMax,
    // The flow's own step list — the review summary resolves "Edit" targets by
    // step ID against this, so per-product sequences stay correct.
    steps,
    profile, // channel-aware panels read ctx.profile (null = plain direct use)
    device,
  };
  const panels = getPanels(steps[stepIndex].id, f, set, ctx);
  const mode = direction;
  const stepValid = mode === "focus" ? (panels[focusPanel] ? panels[focusPanel].valid() : true) : panels.every((p) => p.valid());
  const isLast = stepIndex === steps.length - 1;
  const isFirst = stepIndex === 0 && (mode !== "focus" || focusPanel === 0);
  const frac = (stepIndex + (mode === "focus" ? (focusPanel + 1) / panels.length : 1)) / steps.length;

  useE(() => { if (scrollRef.current) scrollRef.current.scrollTop = 0; setAttempted(false); }, [stepIndex, focusPanel]);

  /* Continue clicked. Valid -> advance. Invalid -> surface every unmet
     question inline and take the applicant to the first one. */
  function attemptNext() {
    if (submitting) return;
    if (stepValid) { setAttempted(false); goNext(); return; }
    setAttempted(true);
    setTimeout(() => { // after the error blocks render
      const root = wrapRef.current || document;
      const bad = root.querySelector('[data-invalid="true"]');
      if (!bad) return;
      try { bad.scrollIntoView({ behavior: "smooth", block: "center" }); } catch { /* older browsers */ }
      const input = bad.querySelector("input:not([type=hidden]):not([disabled]), select, textarea, button.pf-tile");
      if (input && input.focus) { try { input.focus({ preventScroll: true }); } catch { input.focus(); } }
    }, 40);
  }

  function goNext() {
    if (isLast) { doSubmit(); return; }
    setDir("fwd");
    if (mode === "focus" && focusPanel < panels.length - 1) { setFocusPanel((p) => p + 1); }
    else {
      const completedStepId = steps[stepIndex].id;
      setStepIndex((s) => s + 1); setFocusPanel(0);
      if (onStepComplete) { try { onStepComplete(completedStepId, f); } catch (e) { /* non-fatal */ } }
    }
  }
  function goBack() {
    setDir("back");
    if (mode === "focus" && focusPanel > 0) { setFocusPanel((p) => p - 1); }
    else if (stepIndex > 0) {
      const prev = stepIndex - 1;
      setStepIndex(prev);
      if (mode === "focus") { const pp = getPanels(steps[prev].id, f, set, ctx); setFocusPanel(Math.max(0, pp.length - 1)); }
      else setFocusPanel(0);
    }
  }
  async function doSubmit() {
    setSubmitting(true); setError(null);
    try {
      const res = onSubmit ? await onSubmit(f) : null;
      setResult(res || null);
      setSubmitting(false); setDone(true);
    } catch (err) {
      setSubmitting(false);
      setError((err && err.message) || "Something went wrong submitting your application. Please try again.");
    }
  }
  function reset() {
    replace(JSON.parse(JSON.stringify(INITIAL)));
    setDone(false); setStepIndex(0); setFocusPanel(0); setDir("back"); setError(null); setResult(null);
  }

  /* auto-advance in focus mode for single-choice panels */
  const curPanel = panels[focusPanel];
  const autoKey = mode === "focus" && dir === "fwd" && curPanel && AUTO_KEYS.has(curPanel.key) && curPanel.valid();
  useE(() => {
    if (!autoKey) return;
    const t = setTimeout(() => { if (!isLast) goNext(); }, 420);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [autoKey, stepIndex, focusPanel]);

  const ctaLabel = isLast ? (submitting ? "Submitting…" : "Submit my application") : (stepIndex === 0 && (mode !== "focus" || focusPanel === panels.length - 1) ? "Save & continue" : "Continue");
  const animClass = dir === "fwd" ? "pf-anim-in" : "pf-anim-in-back";
  const screenKey = `${stepIndex}-${mode === "focus" ? focusPanel : "x"}`;

  const content = (
    <div key={screenKey} className={animClass}>
      <ScreenContent step={steps[stepIndex]} panels={panels} mode={mode} focusPanel={focusPanel} attempted={attempted} />
    </div>
  );

  const errorNote = error && (
    <div role="alert" style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 12, padding: "12px 14px", background: "var(--coral-tint-2)", border: "1px solid var(--coral)", borderRadius: 12 }}>
      <Icon name="info" size={18} style={{ color: "var(--coral-600)", flexShrink: 0, marginTop: 1 }} />
      <span style={{ fontSize: 13.5, color: "var(--ink-70)", lineHeight: 1.45 }}>{error}</span>
    </div>
  );

  /* ============ MOBILE (narrow container) ============ */
  if (!isWide) {
    return (
      <div ref={wrapRef} className="gd pf-embed" style={{ width: "100%", background: "var(--bg)", position: "relative" }}>
        {done ? <SuccessScreen f={f} onReset={reset} mascot={mascot} reference={result && result.reference} compact /> : (
          <React.Fragment>
            <div style={{ padding: "20px 20px 14px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                {!isFirst ? <button className="pf-back" onClick={goBack} style={{ marginLeft: -10 }}><Icon name="arrowLeft" size={20} /></button> : <Brandmark mascot={mascot} />}
                <div style={{ flex: 1 }} />
                {mascot === "always" && <Llama size={88} />}
              </div>
              <ProgressBar stepIndex={stepIndex} frac={frac} compact steps={steps} saveState={saveState} />
            </div>
            <div ref={scrollRef} style={{ padding: "10px 20px 16px" }}>{content}</div>
            {/* Sticky primary action above the safe-area inset (spec §10.2):
                the Continue button stays reachable however long the step is. */}
            <div style={{ position: "sticky", bottom: 0, zIndex: 20, padding: "12px 20px 20px", paddingBottom: "calc(20px + env(safe-area-inset-bottom))", borderTop: "1px solid var(--line)", background: "rgba(255,255,255,.92)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)" }}>
              {errorNote}
              <Nav onBack={goBack} onNext={attemptNext} isFirst={isFirst} isLast={isLast} valid={stepValid} submitting={submitting} ctaLabel={ctaLabel} />
              <TrustFooter pad="12px 0 2px" product={prodCfg.id} />
            </div>
          </React.Fragment>
        )}
      </div>
    );
  }

  /* ============ DESKTOP (wide container) ============ */
  return (
    <div ref={wrapRef} className="gd pf-embed" style={{ width: "100%", display: "grid", gridTemplateColumns: "360px 1fr", background: "var(--bg)", position: "relative", minHeight: "100vh" }}>
      {/* sidebar stretches to full height (grid align-items: stretch); the
          trust footer is pinned to the bottom via margin-top: auto */}
      <aside style={{ background: "#fff", borderRight: "1px solid var(--line)", padding: "34px 32px", display: "flex", flexDirection: "column", gap: 24, minHeight: "100vh" }}>
        <Brandmark mascot={mascot} big />
        <WelcomeRail mascot={mascot} />
        <StepRail stepIndex={stepIndex} steps={steps} onJump={(i) => { if (i < stepIndex) { setDir("back"); setStepIndex(i); setFocusPanel(0); } }} />
        <div style={{ marginTop: "auto" }}><TrustFooter pad="0" product={prodCfg.id} /></div>
      </aside>
      <main style={{ minWidth: 0 }}>
        {done ? <SuccessScreen f={f} onReset={reset} mascot={mascot} reference={result && result.reference} /> : (
          <React.Fragment>
            <div style={{ padding: "26px 56px 0" }}>
              <div style={{ maxWidth: 600, margin: "0 auto" }}><ProgressBar stepIndex={stepIndex} frac={frac} steps={steps} saveState={saveState} /></div>
            </div>
            <div ref={scrollRef} style={{ padding: "30px 56px 10px" }}>
              <div style={{ maxWidth: 600, margin: "0 auto", width: "100%" }}>{content}</div>
            </div>
            <div style={{ padding: "10px 56px 30px" }}>
              <div style={{ maxWidth: 600, margin: "0 auto" }}>
                {errorNote}
                <Nav onBack={goBack} onNext={attemptNext} isFirst={isFirst} isLast={isLast} valid={stepValid} submitting={submitting} ctaLabel={ctaLabel} />
              </div>
            </div>
          </React.Fragment>
        )}
      </main>
    </div>
  );
}

/* ---------- bits ---------- */
function Brandmark({ mascot, big }) {
  const s = big ? 42 : 36;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <BrandMark size={s} radius={11} style={{ boxShadow: "var(--sh-1)" }} />
      <div>
        <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: big ? 18 : 16, letterSpacing: "-.01em" }}>{BRAND.name}</div>
        {big && <div style={{ fontSize: 12, color: "var(--ink-45)" }}>{BRAND.tagline}</div>}
      </div>
    </div>
  );
}
function WelcomeRail({ mascot }) {
  return (
    <div className="pf-card" style={{ padding: 24, textAlign: "center" }}>
      {mascot !== "off" && <div style={{ display: "flex", justifyContent: "center" }}><Llama size={176} /></div>}
      <h2 style={{ fontSize: 20, marginTop: 10 }}>Let's get you sorted</h2>
      <p style={{ fontSize: 14, color: "var(--ink-70)", marginTop: 8, lineHeight: 1.5 }}>A quick application - most people finish in under two minutes. Your progress saves as you go.</p>
    </div>
  );
}
function StepRail({ stepIndex, onJump, steps }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {steps.map((s, i) => {
        const state = i < stepIndex ? "done" : i === stepIndex ? "active" : "todo";
        return (
          <button key={s.id} onClick={() => state === "done" && onJump(i)} disabled={state !== "done"}
            style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 8px", borderRadius: 10, cursor: state === "done" ? "pointer" : "default", textAlign: "left", background: state === "active" ? "var(--coral-tint-2)" : "transparent" }}>
            <span style={{ width: 26, height: 26, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12.5, fontWeight: 700,
              background: state === "todo" ? "var(--field-2)" : "var(--coral)", color: state === "todo" ? "var(--ink-45)" : "#fff" }}>
              {state === "done" ? <Icon name="check" size={14} /> : s.num}
            </span>
            <span style={{ fontSize: 14, fontWeight: state === "active" ? 600 : 500, color: state === "active" ? "var(--ink)" : state === "todo" ? "var(--ink-45)" : "var(--ink-70)" }}>{s.kicker}</span>
          </button>
        );
      })}
    </div>
  );
}
